<script>
import VerifyEmail from "./VerifyEmail";
import Modal from "@/components/_Shared/Modal";
import WontHaveInstantSupport from "@/components/_Shared/Warnings/WontHaveInstantSupport";

import addressMixin from "@/mixins/addressMixin";
import dateMixin from "@/mixins/dateMixin";
import numberMixin from "@/mixins/numberMixin";
import scrollMixin from "@/mixins/scrollMixin";

import moment from "@/utils/moment";
import { event } from "vue-analytics";
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  props: {
    from: {
      type: String,
      required: true
    },
    withTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    small: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  mixins: [addressMixin, dateMixin, numberMixin, scrollMixin],

  components: {
    Modal,
    VerifyEmail,
    WontHaveInstantSupport
  },

  data() {
    return {
      preloader: false,
      error: false,
      showWontHaveInstantSupportDialog: false
    };
  },

  computed: {
    ...mapState("newOrder", [
      "day",
      "professional",
      "information",
      "address",
      "price",
      "discounted_price",
      "additional_informations",
      "use_my_products",
      "to_be",
      "accept_wont_have_instant_support"
    ]),
    ...mapState("user", ["user"]),
    ...mapGetters("newOrder", [
      "labelDuration",
      "duration",
      "endsOnBusinessTime"
    ]),
    ...mapGetters("user", ["full_name"]),

    totalPrice() {
      return this.discounted_price || this.price;
    },

    mustVerifyEmail() {
      return this.user.status === "waiting_email_verification";
    },

    mustCreatePaymentGateways() {
      return this.address && this.user.status === "email_verified";
    },

    notBusinessDay() {
      return !this.endsOnBusinessTime;
    }
  },

  methods: {
    ...mapActions("newOrder", [
      "sendToCart",
      "create",
      "resetState",
      "applyProfessionalBeginnerCoupon"
    ]),
    ...mapActions("user", [
      "showEmailNotVerifiedDialog",
      "createPaymentGateways"
    ]),

    async submit() {
      this.preloader = true;

      try {
        if (this.notBusinessDay && !this.accept_wont_have_instant_support) {
          this.showWontHaveInstantSupportDialog = true;
          this.preloader = false;
        } else if (this.mustVerifyEmail) {
          this.showEmailNotVerifiedDialog();
        } else if (this.mustCreatePaymentGateways) {
          await this.createPaymentGateways();
        } else await this.createOrder();
      } catch (e) {
        this.$toasted.error(
          "Ocorreu um erro inesperado, entre em contado com o suporte!",
          { duration: 10000 }
        );
        this.preloader = false;
        this.sendEvent("finalizar:error", e);
      }
    },

    async createOrder() {
      try {
        this.sendEvent("finalizar");
        let order = await this.create();
        await this.applyProfessionalBeginnerCoupon(order);
        await this.resetState();
        this.$router.push({
          name: "checkout",
          params: { order_id: order.id }
        });
      } catch (e) {
        throw e;
      }
    },

    sendEvent(action) {
      let days_of_precedence = moment(this.day).diff(moment(), "days") + 1;
      let day = moment(this.day).format("DD/MM/Y");

      let gaString = `Compra:diaespecifico:${action}:dia=${day}:horainicial=${
        this.labelDuration
      }:duracao=${this.duration}:profissional=${
        this.professional.account.first_name
      } ${this.professional.account.last_name}:nota=${
        this.professional.rating
      }:limpezas=undefined:bairro=${
        this.address.district.display_name
      }:cidade=${
        this.address.district.city.display_name
      }:diasdeantecedencia=${days_of_precedence}`;

      if (this.from === "checkout-popup") gaString = gaString + ":frompopup";

      event("cliente", "click", gaString);
    },

    acceptWontHaveInstantSupport() {
      this.showWontHaveInstantSupportDialog = false;
      this.submit();
    }
  },

  watch: {
    "user.status"(status) {
      if (status === "email_verified") this.createPaymentGateways();
      else if (status === "active") this.createOrder();
    }
  }
};
</script>

<template>
  <div id="orders" class="section">
    <div v-if="withTitle" class="section-title">
      CONFIRME SEU PEDIDO
      <hr />
    </div>

    <div class="details mb-20" :class="{small: small}">
      <div class="row">
        <div class="column">
          <span>
            Dia:
            <b>{{day | formatDate("DD/MM/Y")}}</b>
          </span>
          <span>
            Horário:
            <b>{{labelDuration}}</b>
          </span>
          <span>
            Duração:
            <b>{{duration}} {{(duration > 1) ? 'horas' : 'hora'}}</b>
          </span>
        </div>

        <div class="column" style="flex: 1.4">
          <span>
            Profissional:
            <b>{{professional.account.first_name}} {{professional.account.last_name}}</b>
          </span>
          <span>
            Valor:
            <b>{{totalPrice | intToCurrency}}</b>
          </span>
          <span>
            Produtos:
            <b>{{(use_my_products) ? 'Irei usar meus produtos' : 'A diarista levará os produtos'}}</b>
          </span>
        </div>
      </div>

      <div class="column flex-1">
        <b>Local</b>
        <span>{{address.name}}</span>
        <span>{{address | fullAddress}}</span>
      </div>

      <div class="informations">
        <b>Informações adicionais</b>
        <p>{{additional_informations}}</p>
      </div>

      <div class="d-flex justify-center">
        <button
          class="btn bg-green"
          :disabled="preloader"
          :class="{'btn--loading': preloader}"
          @click="submit"
        >
          <span>ir para pagamento</span>
        </button>
      </div>
    </div>

    <transition name="fade" mode="out-in">
      <modal title="OPS! ALGO DEU ERRADO ;/" v-if="error" size="md" @close="error = false">
        <template slot="body">
          <div v-html="error"></div>
        </template>

        <template slot="footer">
          <button class="btn bg-purple-outline" @click="error = false">fechar</button>
        </template>
      </modal>
    </transition>

    <wont-have-instant-support
      :visible="showWontHaveInstantSupportDialog"
      @accept="acceptWontHaveInstantSupport()"
      @reject="showWontHaveInstantSupportDialog = false"
    />
  </div>
</template>

<style scoped>
.covid {
  margin-top: 20px;
  padding: 10px;
  border-radius: 4px;
  box-shadow: 0 0 0 rgba(175, 0, 107, 0.4);
  animation: pulse 2s infinite;
  margin-bottom: 10px;
}
.covid b {
  color: var(--pink);
}

.section {
  margin-bottom: 0;
}

.details {
  width: 460px;
  margin-top: 20px;
  font-size: 13px;
  border: 1px solid var(--green);
  border-radius: 4px;
  padding: 20px;
  transition: all 0.24s ease;
}

.details .row .column {
  flex: 1;
  align-items: flex-start;
  margin: 10px;
  text-align: left;
}

.details .row .column:first-child {
  margin-left: 0;
}

.details .row .column:last-child {
  margin-right: 0;
}

.details .row .column span {
  margin: 2px 0;
}

.details .btn {
  margin-bottom: -40px;
}

.informations {
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-top: 10px;
}

.informations p {
  word-break: break-word;
  margin-left: 20px;
  border-left: 1px solid #ddd;
  padding-left: 10px;
}

@media (max-width: 630px) {
  .row {
    flex-direction: column !important;
  }

  .column {
    margin: 5px 0 !important;
  }

  .details {
    width: calc(100% - 40px);
  }
}

@media (max-width: 365px) {
  .details.small {
    width: 220px;
  }

  .btn.bg-green {
    padding: 12px 15px;
  }
}

@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 rgba(175, 0, 107, 0.4);
    box-shadow: 0 0 0 0 rgba(175, 0, 107, 0.4);
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(175, 0, 107, 0);
    box-shadow: 0 0 0 10px rgba(175, 0, 107, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(175, 0, 107, 0);
    box-shadow: 0 0 0 0 rgba(175, 0, 107, 0);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/PurchaseFlow/Steps/Confirmation.vue